/**
 * `<xa-area>` — area chart (default variant C "overlapping translucent":
 * smooth curves, no dots, vertical gradient fills 32% → 4% opacity,
 * non-stacked). Registered as `xaArea`. A thin composition over `<xa-line>`:
 * it only resolves the area defaults and passes everything through.
 *
 * Bindings (line bindings pass through; user overrides win over area defaults):
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaCategorical` | — | required |
 * | `colors` | `XaPalette` | `'brand'` | global `xaChartsConfig.palette` wins over the default |
 * | `width` | `number` | `520` | SVG px; ignored when `auto-width` is on |
 * | `auto-width` | `boolean` | `false` | re-flow to the container width (responsive) |
 * | `height` | `number` | `260` | SVG px |
 * | `smooth` | `boolean` | `true` | area default (line default is `false`) |
 * | `dots` | `boolean` | `false` | area default (line default is `true`) |
 * | `legend` | `'top' \| 'bottom' \| 'none'` | `'bottom'` | |
 * | `legend-values` | `boolean` | `false` | |
 * | `end-labels` | `boolean` | `false` | |
 * | `max-x-labels` | `number` | `8` | |
 * | `stacked` | `boolean` | `false` | `true` = variant B stacked bands |
 * | `fill` | `'gradient' \| 'flat'` | auto | unset → `'flat'` if `stacked` else `'gradient'` |
 * | `on-series-toggle` | `&` callback | — | `$event: XaSeriesToggleEvent` |
 * | `on-point-hover` | `&` callback | — | `$event: XaHoverEvent` |
 */
import type {
  XaCategorical,
  XaHoverEvent,
  XaLegendPos,
  XaPalette,
  XaSeriesToggleEvent,
} from '@xait/charts-core';
import type { XaCallback } from './chart-base';

class XaAreaController implements ng.IController {
  /* bindings */
  data?: XaCategorical;
  colors?: XaPalette;
  width?: number;
  autoWidth?: boolean;
  height?: number;
  smooth?: boolean;
  dots?: boolean;
  legend?: XaLegendPos;
  legendValues?: boolean;
  endLabels?: boolean;
  maxXLabels?: number;
  rotateXLabels?: boolean;
  zoomX?: boolean;
  maxZoom?: number;
  stacked?: boolean;
  fill?: 'gradient' | 'flat';
  onSeriesToggle!: XaCallback<XaSeriesToggleEvent>;
  onPointHover!: XaCallback<XaHoverEvent>;

  /* effective values passed to <xa-line> (defaults applied without mutating bindings) */
  smoothEff = true;
  dotsEff = false;
  stackedEff = false;
  fillEff: 'gradient' | 'flat' = 'gradient';

  $onInit(): void {
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  private recompute(): void {
    this.smoothEff = this.smooth ?? true;
    this.dotsEff = this.dots ?? false;
    this.stackedEff = this.stacked ?? false;
    this.fillEff = this.fill ?? (this.stackedEff ? 'flat' : 'gradient');
  }
}

export const xaAreaComponent: ng.IComponentOptions = {
  controller: XaAreaController,
  bindings: {
    data: '<',
    colors: '<',
    width: '<',
    autoWidth: '<',
    height: '<',
    smooth: '<',
    dots: '<',
    legend: '<',
    legendValues: '<',
    endLabels: '<',
    maxXLabels: '<',
    rotateXLabels: '<',
    zoomX: '<',
    maxZoom: '<',
    stacked: '<',
    fill: '<',
    onSeriesToggle: '&',
    onPointHover: '&',
  },
  template: `<xa-line
    data="$ctrl.data"
    colors="$ctrl.colors"
    width="$ctrl.width"
    auto-width="$ctrl.autoWidth"
    height="$ctrl.height"
    smooth="$ctrl.smoothEff"
    dots="$ctrl.dotsEff"
    legend="$ctrl.legend"
    legend-values="$ctrl.legendValues"
    end-labels="$ctrl.endLabels"
    max-x-labels="$ctrl.maxXLabels"
    rotate-x-labels="$ctrl.rotateXLabels"
    zoom-x="$ctrl.zoomX"
    max-zoom="$ctrl.maxZoom"
    area="$ctrl.fillEff"
    stacked="$ctrl.stackedEff"
    on-series-toggle="$ctrl.onSeriesToggle({ $event: $event })"
    on-point-hover="$ctrl.onPointHover({ $event: $event })"></xa-line>`,
};
